/**
 * UI-8b Task 3 — useTenantLimits hook.
 *
 * Single-resource (not list) hook for the per-tenant limit override row.
 * Wraps `GET /platform/tenants/:id/limits` (returns `{ limits, planDefaults }`)
 * and `PATCH /platform/tenants/:id/limits` (returns `{ limits }`).
 *
 * The backend response merges the optional `tenant_limits` row with plan-default
 * values resolved from the tenant's currently-assigned plan. The 7-field
 * tenant_limits row (per UI-8b §0.A revision) is exposed verbatim — the UI
 * card decides which fields fall back to plan defaults vs table defaults.
 *
 * Permission: backend gates with `PLATFORM_TENANTS_MANAGE`. The hook imposes
 * no client-side ACL — pages wrap themselves in `<RequirePermission>`.
 */
import { useCallback, useEffect, useState } from 'react'
import { api } from '../api/client.js'

export interface TenantLimits {
  tenantId: string
  maxAgents: number
  maxConcurrentCalls: number
  maxCallsPerMinute: number
  maxDialerOriginatesPerSecond: number
  maxStorageGb: number
  maxApiRequestsPerMinute: number
  maxIvrConcurrent: number
  updatedAt: string
}

export interface TenantLimitsPlanDefaults {
  maxAgents: number
  maxConcurrentCalls: number
}

export interface TenantLimitsResponse {
  limits: TenantLimits | null
  planDefaults: TenantLimitsPlanDefaults | null
}

export interface UseTenantLimitsResult {
  data: TenantLimitsResponse | null
  loading: boolean
  error: string | null
  refetch: () => Promise<void>
  save: (patch: Partial<TenantLimits>) => Promise<void>
}

export function useTenantLimits(tenantId: string): UseTenantLimitsResult {
  const [data, setData] = useState<TenantLimitsResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async (): Promise<void> => {
    setLoading(true)
    setError(null)
    try {
      const res = await api.get<TenantLimitsResponse>(
        `/platform/tenants/${tenantId}/limits`,
      )
      setData(res)
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Failed to load tenant limits',
      )
    } finally {
      setLoading(false)
    }
  }, [tenantId])

  useEffect(() => {
    void load()
  }, [load])

  const save = useCallback(
    async (patch: Partial<TenantLimits>): Promise<void> => {
      await api.patch(`/platform/tenants/${tenantId}/limits`, patch)
      await load()
    },
    [tenantId, load],
  )

  return { data, loading, error, refetch: load, save }
}
